/** Light enforcement layer for the constraint system (issue #30).
 *  Runs on the commit boundary of direct edits — when the user
 *  drags an anchor or translates a whole construction line, we
 *  propagate Snap (coincident) constraints by rigidly translating
 *  every connected partner line. Chains of Snap constraints are
 *  walked BFS-style so an entire group of snap-connected lines
 *  moves together as a rigid body. Translation preserves the
 *  partner's parametric primitive (rect / ellipse / pill) — the
 *  whole-object move is the point of this enforcement.
 *
 *  Why paper.js here?  The construction-line pathData is the
 *  canonical store of geometry. Reading a specific segment's point
 *  and translating the whole path are both cleanest with paper's
 *  parsed representation. */

import type paper from "paper";
import type { Constraint, ConstraintRef, Project } from "./Project";
import { projectConstraints } from "./constraints";
import type { ConstructionLine } from "./Project";
import { translateConstructionLine } from "./constructionTranslate";

/** True when `pathData` contains at least one closed subpath. Paper
 *  serialises closed paths with a trailing `z`/`Z` command, so a plain
 *  string scan is enough — no parse needed. Open sketches (polylines,
 *  unclosed freehand strokes) have no addressable "shape center";
 *  closed ones do (#417). */
export function hasClosedSubpath(pathData: string): boolean {
  return /[zZ]/.test(pathData);
}

/** Memo for pathData-derived AABBs of non-primitive lines. Keyed by
 *  the line object and validated against its `pathData`, so a stale
 *  entry from a superseded snapshot can never leak a wrong bbox —
 *  any geometry change produces a new pathData string. Keeps the
 *  per-frame snap scans from re-parsing unchanged freehand paths. */
const pathAabbMemo = new WeakMap<
  ConstructionLine,
  {
    pathData: string;
    aabb: { x: number; y: number; width: number; height: number } | null;
  }
>();

/** AABB a snap target can be derived from: the parametric primitive's
 *  box when the line still has one (rectangle / ellipse / pill —
 *  cheap, no parsing), otherwise — given a paper scope — the bounds
 *  of the line's pathData, offered only for closed shapes (freehand
 *  loops, polygons, primitives edited away; issue #417). Open lines
 *  return null: an open stroke has no shape center. */
function getLineAabb(
  line: ConstructionLine,
  scope?: paper.PaperScope,
): { x: number; y: number; width: number; height: number } | null {
  const prim = getPrimitiveAabb(line);
  if (prim) return prim;
  if (!scope || !hasClosedSubpath(line.pathData)) return null;
  const memo = pathAabbMemo.get(line);
  if (memo && memo.pathData === line.pathData) return memo.aabb;
  let aabb: { x: number; y: number; width: number; height: number } | null =
    null;
  try {
    const cp = new scope.CompoundPath({
      pathData: line.pathData,
      insert: false,
    });
    const b = cp.bounds;
    aabb = { x: b.x, y: b.y, width: b.width, height: b.height };
    cp.remove();
  } catch {
    aabb = null;
  }
  pathAabbMemo.set(line, { pathData: line.pathData, aabb });
  return aabb;
}

/** Compute the bbox center of a construction line. For lines with a
 *  parametric primitive (rectangle, ellipse, pill) this reads the
 *  primitive metadata directly. For non-primitive lines a `scope` is
 *  required and the center comes from the pathData bounds — but only
 *  for CLOSED shapes (freehand loops, polygons; issue #417): an open
 *  stroke has no addressable center and returns null, as do
 *  non-primitive calls without a scope. Returns `{ x, y }` in
 *  project mm. */
export function getBboxCenter(
  line: ConstructionLine,
  scope?: paper.PaperScope,
): { x: number; y: number } | null {
  const aabb = getLineAabb(line, scope);
  if (!aabb) return null;
  return {
    x: aabb.x + aabb.width / 2,
    y: aabb.y + aabb.height / 2,
  };
}

export type BboxEdge = "top" | "right" | "bottom" | "left";

export const BBOX_EDGES: readonly BboxEdge[] = [
  "top",
  "right",
  "bottom",
  "left",
];

/** Compute the midpoint of one of the 4 bbox edges of a line. Same
 *  sourcing rules as `getBboxCenter`: primitive metadata when the
 *  line has it, otherwise (with a scope) the pathData bounds of a
 *  closed shape (#417); null for open non-primitive lines.
 *
 *  Pill bboxes are oriented capsules: we use the axis-aligned
 *  bounding box of the capsule (capsule extents plus the radius in
 *  every direction), so a tilted pill's midpoints land on its AABB
 *  edges — visually consistent with rectangle / ellipse midpoints. */
export function getBboxMidpoint(
  line: ConstructionLine,
  edge: BboxEdge,
  scope?: paper.PaperScope,
): { x: number; y: number } | null {
  const aabb = getLineAabb(line, scope);
  if (!aabb) return null;
  const cx = aabb.x + aabb.width / 2;
  const cy = aabb.y + aabb.height / 2;
  switch (edge) {
    case "top":
      return { x: cx, y: aabb.y };
    case "bottom":
      return { x: cx, y: aabb.y + aabb.height };
    case "left":
      return { x: aabb.x, y: cy };
    case "right":
      return { x: aabb.x + aabb.width, y: cy };
  }
}

/** AABB of a primitive line in project mm, or null for non-primitive
 *  lines. Pill AABB is the tight bbox of the (possibly rotated)
 *  capsule: the convex hull of the two hemispheres around each axis
 *  endpoint. */
function getPrimitiveAabb(
  line: ConstructionLine,
): { x: number; y: number; width: number; height: number } | null {
  const prim = line.primitive;
  if (!prim) return null;
  if (prim.kind === "rectangle" || prim.kind === "ellipse") {
    return { x: prim.x, y: prim.y, width: prim.width, height: prim.height };
  }
  if (prim.kind === "pill") {
    const r = prim.width / 2;
    const minX = Math.min(prim.x1, prim.x2) - r;
    const maxX = Math.max(prim.x1, prim.x2) + r;
    const minY = Math.min(prim.y1, prim.y2) - r;
    const maxY = Math.max(prim.y1, prim.y2) + r;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
  return null;
}

/** Read the absolute position of an anchor inside `pathData`. Returns
 *  null if `pathData` doesn't parse or the indices are out of range. */
export function getAnchorPosition(
  pathData: string,
  pathIndex: number,
  segmentIndex: number,
  scope: paper.PaperScope,
): { x: number; y: number } | null {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const subpaths = subpathsOf(cp);
  const sub = subpaths[pathIndex];
  if (!sub) {
    cp.remove();
    return null;
  }
  const seg = sub.segments[segmentIndex];
  if (!seg) {
    cp.remove();
    return null;
  }
  const pos = { x: seg.point.x, y: seg.point.y };
  cp.remove();
  return pos;
}

/** Return a new `pathData` string with the anchor at
 *  `(pathIndex, segmentIndex)` moved to `pos`. Other anchors and the
 *  segment's handles are preserved (paper moves only `seg.point`).
 *  Returns the input unchanged if anything fails to parse / resolve. */
export function setAnchorPosition(
  pathData: string,
  pathIndex: number,
  segmentIndex: number,
  pos: { x: number; y: number },
  scope: paper.PaperScope,
): string {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return pathData;
  }
  const subpaths = subpathsOf(cp);
  const sub = subpaths[pathIndex];
  if (!sub) {
    cp.remove();
    return pathData;
  }
  const seg = sub.segments[segmentIndex];
  if (!seg) {
    cp.remove();
    return pathData;
  }
  seg.point = new scope.Point(pos.x, pos.y);
  const result = cp.pathData;
  cp.remove();
  return result;
}

function subpathsOf(cp: paper.CompoundPath): paper.Path[] {
  return cp.children && cp.children.length > 0
    ? (cp.children as paper.Path[])
    : [cp as unknown as paper.Path];
}

/** Resolve every anchor `ConstraintRef` that *partners* a freshly-
 *  dragged anchor across a coincident constraint. Walks each
 *  coincident constraint that mentions the dragged anchor as one of
 *  its refs, then yields the *other* anchor refs in that constraint
 *  (a constraint may have two anchors or — uncommon but legal —
 *  three or more, all coincident at the same point). Non-anchor
 *  refs are skipped (line / curve coincident is its own kind).
 *  Non-coincident constraints are ignored (their enforcement rules
 *  live elsewhere). */
export function findCoincidentPartners(
  project: Project,
  draggedConstructionId: string,
  draggedPathIndex: number,
  draggedSegmentIndex: number,
): Array<{
  constructionId: string;
  pathIndex: number;
  segmentIndex: number;
}> {
  const partners: Array<{
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
  }> = [];
  for (const c of projectConstraints(project)) {
    if (c.kind !== "coincident") continue;
    const involvesDragged = c.refs.some((r) =>
      isMatchingAnchorRef(
        r,
        draggedConstructionId,
        draggedPathIndex,
        draggedSegmentIndex,
      ),
    );
    if (!involvesDragged) continue;
    for (const r of c.refs) {
      if (r.kind !== "anchor") continue;
      if (
        isMatchingAnchorRef(
          r,
          draggedConstructionId,
          draggedPathIndex,
          draggedSegmentIndex,
        )
      )
        continue;
      partners.push({
        constructionId: r.constructionId,
        pathIndex: r.pathIndex,
        segmentIndex: r.segmentIndex,
      });
    }
  }
  return partners;
}

function isMatchingAnchorRef(
  ref: ConstraintRef,
  constructionId: string,
  pathIndex: number,
  segmentIndex: number,
): boolean {
  return (
    ref.kind === "anchor" &&
    ref.constructionId === constructionId &&
    ref.pathIndex === pathIndex &&
    ref.segmentIndex === segmentIndex
  );
}

export interface AnchorRefLite {
  kind: "anchor";
  constructionId: string;
  pathIndex: number;
  segmentIndex: number;
}

/** A reference to the bbox centre of a parametric primitive line.
 *  Snap-pipeline counterpart to the model's `center` ConstraintRef
 *  kind. Carries no per-anchor indices — the centre is derived from
 *  the line's primitive metadata, not from a particular segment. */
export interface CenterRefLite {
  kind: "center";
  constructionId: string;
}

/** A reference to one of the 4 bbox-edge midpoints of a parametric
 *  primitive line. Same lifecycle and semantics as `CenterRefLite` —
 *  the position is derived from primitive metadata, so the ref is
 *  only meaningful while the line still has a primitive. */
export interface BboxMidpointRefLite {
  kind: "bbox-midpoint";
  constructionId: string;
  edge: BboxEdge;
}

/** A reference to the midpoint of one straight segment of a line —
 *  i.e. the centre of a single line / line-segment between two
 *  consecutive anchors (issue #377). `segmentIndex` is the index of the
 *  segment's first anchor; the midpoint sits halfway to the next anchor
 *  (wrapping to anchor 0 on a closed subpath). Snap-pipeline only — it
 *  is a positional snap target, not (yet) a persistable constraint
 *  ref, so it never round-trips through `ConstraintRef`. */
export interface SegmentMidpointRefLite {
  kind: "segment-midpoint";
  constructionId: string;
  pathIndex: number;
  segmentIndex: number;
}

/** Anything the snap pipeline can pin: a concrete anchor, a
 *  primitive's bbox centre, one of its 4 bbox-edge midpoints, or the
 *  centre of a straight segment. Internal counterpart to the model's
 *  `ConstraintRef` (which also includes `line` and `curve` kinds used
 *  by constraint kinds we don't enforce yet). The `segment-midpoint`
 *  kind has no `ConstraintRef` equivalent — it's a position-only snap
 *  target. */
export type SnapRefLite =
  | AnchorRefLite
  | CenterRefLite
  | BboxMidpointRefLite
  | SegmentMidpointRefLite;

function snapRefKey(r: SnapRefLite): string {
  if (r.kind === "anchor") {
    return `a:${r.constructionId}:${r.pathIndex}:${r.segmentIndex}`;
  }
  if (r.kind === "center") return `c:${r.constructionId}`;
  if (r.kind === "segment-midpoint") {
    return `sm:${r.constructionId}:${r.pathIndex}:${r.segmentIndex}`;
  }
  return `m:${r.constructionId}:${r.edge}`;
}

function snapRefEq(a: SnapRefLite, b: SnapRefLite): boolean {
  return snapRefKey(a) === snapRefKey(b);
}

/** Resolve a snap-ref to its current project-mm position. Anchors
 *  read from the line's pathData; centres come from primitive
 *  metadata. Returns null when the line isn't found or the ref's
 *  indices are out of range. */
export function getSnapRefPosition(
  ref: SnapRefLite,
  line: ConstructionLine | undefined,
  scope: paper.PaperScope,
): { x: number; y: number } | null {
  if (!line) return null;
  if (ref.kind === "center") return getBboxCenter(line, scope);
  if (ref.kind === "bbox-midpoint")
    return getBboxMidpoint(line, ref.edge, scope);
  if (ref.kind === "segment-midpoint") {
    return getSegmentMidpoint(
      line.pathData,
      ref.pathIndex,
      ref.segmentIndex,
      scope,
    );
  }
  return getAnchorPosition(
    line.pathData,
    ref.pathIndex,
    ref.segmentIndex,
    scope,
  );
}

/** Midpoint between anchor `segmentIndex` and the next anchor on the
 *  same subpath (wrapping to anchor 0 when the subpath is closed). The
 *  geometric centre of one straight segment / line (issue #377).
 *  Returns null when the data can't be parsed or the indices are out
 *  of range / there is no following anchor on an open subpath. */
export function getSegmentMidpoint(
  pathData: string,
  pathIndex: number,
  segmentIndex: number,
  scope: paper.PaperScope,
): { x: number; y: number } | null {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const sub = subpathsOf(cp)[pathIndex];
  if (!sub) {
    cp.remove();
    return null;
  }
  const a = sub.segments[segmentIndex];
  const nextIndex =
    segmentIndex + 1 < sub.segments.length
      ? segmentIndex + 1
      : sub.closed
        ? 0
        : -1;
  const b = nextIndex >= 0 ? sub.segments[nextIndex] : undefined;
  if (!a || !b) {
    cp.remove();
    return null;
  }
  const mid = {
    x: (a.point.x + b.point.x) / 2,
    y: (a.point.y + b.point.y) / 2,
  };
  cp.remove();
  return mid;
}

/** Search for an anchor on a *different* construction line within
 *  `thresholdMm` of any of the supplied `sourceAnchors`. Used by the
 *  Construction tool's per-frame snap-during-drag logic to find a
 *  candidate the user is about to lock onto. Returns the closest
 *  matching pair (source ↔ candidate) or null when nothing's within
 *  range. Anchors on the same line as a source are skipped — the
 *  user can't snap an anchor onto its own line.
 *
 *  `extraExcludedLineIds` lets callers suppress candidates on lines
 *  that are visually moving along with the drag (chain partners
 *  pulled by `liveChainTranslate`). Without this, partners' model
 *  positions — frozen at pre-drag — would still register as snap
 *  candidates even though the partner's rendered geometry is
 *  somewhere else, leaving "ghost" snap points behind (issue #102). */
export function findNearestSnapCandidate(
  lines: ConstructionLine[],
  sourceAnchors: Array<{
    ref: SnapRefLite;
    pos: { x: number; y: number };
  }>,
  thresholdMm: number,
  scope: paper.PaperScope,
  extraExcludedLineIds?: ReadonlySet<string>,
): {
  sourceRef: SnapRefLite;
  candidateRef: SnapRefLite;
  candidatePos: { x: number; y: number };
  distance: number;
} | null {
  if (sourceAnchors.length === 0) return null;
  const excludedLineIds = new Set(
    sourceAnchors.map((s) => s.ref.constructionId),
  );
  if (extraExcludedLineIds) {
    for (const id of extraExcludedLineIds) excludedLineIds.add(id);
  }
  let best: {
    sourceRef: SnapRefLite;
    candidateRef: SnapRefLite;
    candidatePos: { x: number; y: number };
    distance: number;
  } | null = null;
  const tryCandidate = (
    cpos: { x: number; y: number },
    candidateRef: SnapRefLite,
  ) => {
    for (const src of sourceAnchors) {
      const d = Math.hypot(cpos.x - src.pos.x, cpos.y - src.pos.y);
      if (d <= thresholdMm && (best === null || d < best.distance)) {
        best = {
          sourceRef: src.ref,
          candidateRef,
          candidatePos: cpos,
          distance: d,
        };
      }
    }
  };
  for (const line of lines) {
    if (excludedLineIds.has(line.id)) continue;
    if (line.visible === false) continue;
    let cp: paper.CompoundPath | null = null;
    try {
      cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
    } catch {
      continue;
    }
    const subs = subpathsOf(cp);
    // Segment midpoints are offered for the centre of a single line and
    // of each straight segment of a polyline / polygon (issue #377).
    // They're skipped for rectangle / ellipse / pill primitives, whose
    // edge midpoints + centre already come from the bbox candidates
    // below (and whose ellipse "segments" are curved arcs, not lines).
    const offerSegmentMidpoints =
      !line.primitive || line.primitive.kind === "line";
    subs.forEach((sub, pathIndex) => {
      sub.segments.forEach((seg, segmentIndex) => {
        tryCandidate(
          { x: seg.point.x, y: seg.point.y },
          {
            kind: "anchor",
            constructionId: line.id,
            pathIndex,
            segmentIndex,
          },
        );
        if (!offerSegmentMidpoints) return;
        // The segment running from this anchor to the next one on the
        // subpath (wrapping to anchor 0 when closed). Only STRAIGHT
        // segments get a midpoint candidate — a curved segment's
        // straight-line midpoint wouldn't lie on the path.
        const nextIndex =
          segmentIndex + 1 < sub.segments.length
            ? segmentIndex + 1
            : sub.closed
              ? 0
              : -1;
        if (nextIndex < 0) return;
        const next = sub.segments[nextIndex]!;
        if (!seg.handleOut.isZero() || !next.handleIn.isZero()) return;
        tryCandidate(
          {
            x: (seg.point.x + next.point.x) / 2,
            y: (seg.point.y + next.point.y) / 2,
          },
          {
            kind: "segment-midpoint",
            constructionId: line.id,
            pathIndex,
            segmentIndex,
          },
        );
      });
    });
    cp.remove();
    // The bbox center is also a snap target: from primitive metadata
    // for rect / ellipse / pill lines, and from the pathData bounds
    // for closed non-primitive shapes — freehand loops and polygons
    // (issue #417). Open lines contribute nothing here.
    const center = getBboxCenter(line, scope);
    if (center) {
      tryCandidate(center, { kind: "center", constructionId: line.id });
      // Each of the 4 bbox-edge midpoints is also an addressable
      // snap target — same lifecycle as the center.
      // `findNearestSnapCandidate` picks the single closest among
      // anchor / center / midpoint via `tryCandidate`, so a shape
      // contributes 5 candidates (4 edges + center) alongside its
      // anchors.
      for (const edge of BBOX_EDGES) {
        const mid = getBboxMidpoint(line, edge, scope);
        if (mid) {
          tryCandidate(mid, {
            kind: "bbox-midpoint",
            constructionId: line.id,
            edge,
          });
        }
      }
    }
  }
  return best;
}

/** Scan every coincident constraint and return the IDs of any that
 *  are now broken — i.e., their referenced anchors no longer share
 *  a position within `epsilonMm`. Used after an edit to detect
 *  pairs the drag tore apart (the typical case is a vertex drag
 *  whose chain pulls a partner line, but the chain loops back to a
 *  *different* anchor on the dragged line — that anchor stays put,
 *  so the partner's other-side anchor drifts off). Callers then
 *  drop those constraints from the project. */
export function findBrokenCoincidentConstraints(
  proj: Project,
  lines: ConstructionLine[],
  scope: paper.PaperScope,
  epsilonMm = 0.001,
): Set<string> {
  const lineMap = new Map<string, ConstructionLine>();
  for (const l of lines) lineMap.set(l.id, l);
  const broken = new Set<string>();
  for (const c of projectConstraints(proj)) {
    if (c.kind !== "coincident") continue;
    const positions: Array<{ x: number; y: number }> = [];
    for (const r of c.refs) {
      if (r.kind !== "anchor") continue;
      const line = lineMap.get(r.constructionId);
      if (!line) continue;
      const pos = getAnchorPosition(
        line.pathData,
        r.pathIndex,
        r.segmentIndex,
        scope,
      );
      if (pos) positions.push(pos);
    }
    if (positions.length < 2) continue;
    const first = positions[0]!;
    for (let i = 1; i < positions.length; i++) {
      const pi = positions[i]!;
      if (Math.hypot(pi.x - first.x, pi.y - first.y) > epsilonMm) {
        broken.add(c.id);
        break;
      }
    }
  }
  return broken;
}

/** True when `proj` already has a coincident constraint pairing
 *  the two snap refs. Handles both anchor and centre refs. Used to
 *  avoid duplicating constraints when a snap-on-release tries to
 *  add a pair the user has already pinned. */
export function hasCoincidentConstraint(
  proj: Project,
  a: SnapRefLite,
  b: SnapRefLite,
): boolean {
  const refMatches = (r: ConstraintRef, target: SnapRefLite): boolean => {
    if (target.kind === "anchor") {
      return (
        r.kind === "anchor" &&
        r.constructionId === target.constructionId &&
        r.pathIndex === target.pathIndex &&
        r.segmentIndex === target.segmentIndex
      );
    }
    if (target.kind === "center") {
      return r.kind === "center" && r.constructionId === target.constructionId;
    }
    // Segment-midpoints are position-only snap targets with no
    // `ConstraintRef` form (issue #377) — they never appear in a stored
    // constraint, so nothing matches.
    if (target.kind === "segment-midpoint") return false;
    return (
      r.kind === "bbox-midpoint" &&
      r.constructionId === target.constructionId &&
      r.edge === target.edge
    );
  };
  for (const c of projectConstraints(proj)) {
    if (c.kind !== "coincident") continue;
    let foundA = false;
    let foundB = false;
    for (const r of c.refs) {
      if (refMatches(r, a)) foundA = true;
      else if (refMatches(r, b)) foundB = true;
    }
    if (foundA && foundB) return true;
  }
  return false;
}

/** Strip every coincident constraint whose `center` or `bbox-midpoint`
 *  ref points at a line whose bbox is no longer addressable: the line
 *  is gone, or it has neither a primitive nor a closed subpath (an
 *  open stroke has no shape center). A primitive edited into a closed
 *  polygon / freehand loop KEEPS its center constraints — its bbox is
 *  still well-defined from the pathData (#417). Called by every commit
 *  that might drop a line's primitive or open its geometry (vertex
 *  drag, bezier-handle drag, curve drag, segment / anchor delete). */
export function pruneInvalidCenterConstraints(
  lines: ConstructionLine[],
  constraints: Constraint[] | undefined,
): Constraint[] | undefined {
  if (!constraints || constraints.length === 0) return constraints;
  const lineMap = new Map<string, ConstructionLine>();
  for (const l of lines) lineMap.set(l.id, l);
  let changed = false;
  const kept = constraints.filter((c) => {
    if (c.kind !== "coincident") return true;
    for (const r of c.refs) {
      if (r.kind !== "center" && r.kind !== "bbox-midpoint") continue;
      const line = lineMap.get(r.constructionId);
      if (!line || (!line.primitive && !hasClosedSubpath(line.pathData))) {
        changed = true;
        return false;
      }
    }
    return true;
  });
  return changed ? kept : constraints;
}

/** Closure of `originIds` under the Snap (coincident) graph: every
 *  line reachable from any origin via one or more snap edges. Used
 *  by per-frame drag snap-candidate scans to skip lines that are
 *  being chain-translated alongside the drag — without this, those
 *  partners' pre-drag model positions register as ghost snap
 *  candidates (issue #102). Includes the origins themselves so the
 *  caller can pass the result straight in as the exclusion set. */
export function chainReachableLines(
  proj: Project,
  originIds: ReadonlyArray<string>,
): Set<string> {
  const visited = new Set<string>(originIds);
  if (visited.size === 0) return visited;
  const edges = snapEdges(proj);
  if (edges.length === 0) return visited;
  const adj = new Map<string, Set<string>>();
  for (const e of edges) {
    if (!adj.has(e.a.constructionId)) adj.set(e.a.constructionId, new Set());
    if (!adj.has(e.b.constructionId)) adj.set(e.b.constructionId, new Set());
    adj.get(e.a.constructionId)!.add(e.b.constructionId);
    adj.get(e.b.constructionId)!.add(e.a.constructionId);
  }
  const queue: string[] = [...visited];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const nbrs = adj.get(cur);
    if (!nbrs) continue;
    for (const n of nbrs) {
      if (visited.has(n)) continue;
      visited.add(n);
      queue.push(n);
    }
  }
  return visited;
}

/** Snap-graph edge: a pair of anchors on *different* lines that a
 *  coincident constraint pins together. Same-line refs are excluded
 *  — within-line coincidence is satisfied automatically when the
 *  line translates rigidly. */
export function snapEdges(
  proj: Project,
): Array<{ a: SnapRefLite; b: SnapRefLite }> {
  const edges: Array<{ a: SnapRefLite; b: SnapRefLite }> = [];
  for (const c of projectConstraints(proj)) {
    if (c.kind !== "coincident") continue;
    // Coincident now accepts both anchor and centre refs. Collect
    // every snap-typed ref and emit one edge per pair on
    // different lines — same-line pairs are skipped because their
    // "constraint" is trivially preserved by rigid translation of
    // the owning line.
    const refs: SnapRefLite[] = [];
    for (const r of c.refs) {
      if (r.kind === "anchor") {
        refs.push({
          kind: "anchor",
          constructionId: r.constructionId,
          pathIndex: r.pathIndex,
          segmentIndex: r.segmentIndex,
        });
      } else if (r.kind === "center") {
        refs.push({
          kind: "center",
          constructionId: r.constructionId,
        });
      } else if (r.kind === "bbox-midpoint") {
        refs.push({
          kind: "bbox-midpoint",
          constructionId: r.constructionId,
          edge: r.edge,
        });
      }
    }
    for (let i = 0; i < refs.length; i++) {
      for (let j = i + 1; j < refs.length; j++) {
        if (refs[i]!.constructionId === refs[j]!.constructionId) continue;
        edges.push({ a: refs[i]!, b: refs[j]! });
      }
    }
  }
  return edges;
}

/** Enumerate every anchor on a construction line as
 *  `(pathIndex, segmentIndex)` tuples — used to spread "freshly
 *  translated" anchors across the BFS frontier so chained snap
 *  constraints can drive further translations. */
function allAnchorsOnLine(
  line: ConstructionLine,
  scope: paper.PaperScope,
): Array<{ pathIndex: number; segmentIndex: number }> {
  const out: Array<{ pathIndex: number; segmentIndex: number }> = [];
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
  } catch {
    return out;
  }
  const subs = subpathsOf(cp);
  subs.forEach((sub, pathIndex) => {
    sub.segments.forEach((_seg, segmentIndex) => {
      out.push({ pathIndex, segmentIndex });
    });
  });
  cp.remove();
  return out;
}

/** Run a BFS across the snap (coincident-anchor) graph and rigidly
 *  translate every line reachable from the initial active anchors.
 *  `originLineIds` are pinned in place — they're the user's direct
 *  edit (already at their intended post-edit state) and must not be
 *  translated again. Each step computes the required translation
 *  from the *current* state, so chains converge correctly even when
 *  the seed delta is heterogeneous.
 *
 *  `draggedAnchor`, when supplied, enables *chain-through-origin*:
 *  if the BFS reaches a coincident pair whose other side sits on
 *  the dragged-vertex's origin line at a non-dragged anchor, that
 *  anchor is moved (just that segment's point in the origin's
 *  pathData) so the pair stays satisfied — instead of leaving the
 *  pair broken. The pulled anchor itself becomes a chain source so
 *  further edges propagate from it. Pass `null` for the line-move
 *  case: the whole origin translates uniformly and no sibling-
 *  anchor pull is needed. */
function applySnapChainTranslation(
  proj: Project,
  linesAfter: ConstructionLine[],
  activeAnchors: SnapRefLite[],
  originLineIds: Set<string>,
  scope: paper.PaperScope,
  draggedAnchor?: AnchorRefLite | null,
): ConstructionLine[] {
  const edges = snapEdges(proj);
  if (edges.length === 0) return linesAfter;
  const state = new Map<string, ConstructionLine>();
  for (const l of linesAfter) state.set(l.id, l);
  const visited = new Set<string>(originLineIds);
  // Per-anchor pulled set for chain-through-origin / -partner so the
  // same anchor doesn't get tugged twice when the chain reaches it via
  // two different edges. Keyed by snap-ref signature so
  // anchor and centre refs share the namespace.
  const pulledAnchors = new Set<string>();
  const isDraggedAnchor = (a: SnapRefLite) =>
    a.kind === "anchor" &&
    !!draggedAnchor &&
    a.constructionId === draggedAnchor.constructionId &&
    a.pathIndex === draggedAnchor.pathIndex &&
    a.segmentIndex === draggedAnchor.segmentIndex;
  const queue: SnapRefLite[] = [...activeAnchors];
  let changed = false;
  while (queue.length > 0) {
    const src = queue.shift()!;
    const srcLine = state.get(src.constructionId);
    if (!srcLine) continue;
    for (const e of edges) {
      let other: SnapRefLite | null = null;
      if (snapRefEq(e.a, src)) other = e.b;
      else if (snapRefEq(e.b, src)) other = e.a;
      if (!other) continue;
      if (visited.has(other.constructionId)) {
        // Chain-through-partner (#1166): a partner already moved by an
        // earlier edge is reached again through another of its links, at
        // an anchor that no longer sits on its ref (a line linked to two
        // corners of a resized rectangle). Move just that anchor onto the
        // ref so every link stays attached — the partner stretches rather
        // than one end breaking free. Origin lines are never reshaped
        // here: the user's edit is final (they're handled below, and only
        // for a vertex drag). A partner that stops matching its primitive
        // is settled by the caller (`settleSnapReshapedLines`).
        if (!originLineIds.has(other.constructionId)) {
          if (other.kind !== "anchor") continue;
          const key = snapRefKey(other);
          if (pulledAnchors.has(key)) continue;
          const partnerLine = state.get(other.constructionId);
          if (!partnerLine) continue;
          const srcPos = getSnapRefPosition(src, srcLine, scope);
          const otherPos = getSnapRefPosition(other, partnerLine, scope);
          if (!srcPos || !otherPos) continue;
          if (
            Math.abs(srcPos.x - otherPos.x) < 1e-6 &&
            Math.abs(srcPos.y - otherPos.y) < 1e-6
          ) {
            continue;
          }
          const newPathData = setAnchorPosition(
            partnerLine.pathData,
            other.pathIndex,
            other.segmentIndex,
            { x: srcPos.x, y: srcPos.y },
            scope,
          );
          if (newPathData === partnerLine.pathData) continue;
          state.set(other.constructionId, {
            ...partnerLine,
            pathData: newPathData,
          });
          pulledAnchors.add(key);
          changed = true;
          queue.push(other);
          continue;
        }
        // Chain-through-origin: when the chain loops back to the
        // dragged-vertex origin line at a *non-dragged* anchor,
        // tug that anchor along so the pair stays coincident
        // instead of breaking. Skipped for centre refs on origin
        // (the constraint will be pruned on commit when the
        // primitive drops); for the dragged anchor itself (the
        // user's hand owns its position); and for line-move
        // (no `draggedAnchor`).
        if (!draggedAnchor) continue;
        if (other.constructionId !== draggedAnchor.constructionId) continue;
        if (other.kind !== "anchor") continue;
        if (isDraggedAnchor(other)) continue;
        const key = snapRefKey(other);
        if (pulledAnchors.has(key)) continue;
        const partnerLine = state.get(other.constructionId);
        if (!partnerLine) continue;
        const srcPos = getSnapRefPosition(src, srcLine, scope);
        const otherPos = getSnapRefPosition(other, partnerLine, scope);
        if (!srcPos || !otherPos) continue;
        const newPathData = setAnchorPosition(
          partnerLine.pathData,
          other.pathIndex,
          other.segmentIndex,
          { x: srcPos.x, y: srcPos.y },
          scope,
        );
        if (newPathData === partnerLine.pathData) continue;
        state.set(other.constructionId, {
          ...partnerLine,
          pathData: newPathData,
        });
        pulledAnchors.add(key);
        changed = true;
        // Continue BFS from the pulled origin anchor — edges
        // touching it (to yet-unvisited lines) should propagate.
        queue.push(other);
        continue;
      }
      const partnerLine = state.get(other.constructionId);
      if (!partnerLine) continue;
      const srcPos = getSnapRefPosition(src, srcLine, scope);
      const partnerPos = getSnapRefPosition(other, partnerLine, scope);
      if (!srcPos || !partnerPos) continue;
      const dx = srcPos.x - partnerPos.x;
      const dy = srcPos.y - partnerPos.y;
      const translated = translateConstructionLine(partnerLine, dx, dy, scope);
      state.set(other.constructionId, translated);
      visited.add(other.constructionId);
      changed = changed || translated !== partnerLine;
      // Every snap-ref on the partner is now "active" — anchors
      // and (if a primitive) the centre. Enqueue them so the
      // chain can keep walking through any edge touching them.
      for (const a of allAnchorsOnLine(translated, scope)) {
        queue.push({
          kind: "anchor",
          constructionId: other.constructionId,
          pathIndex: a.pathIndex,
          segmentIndex: a.segmentIndex,
        });
      }
      if (translated.primitive) {
        queue.push({
          kind: "center",
          constructionId: other.constructionId,
        });
        for (const edge of BBOX_EDGES) {
          queue.push({
            kind: "bbox-midpoint",
            constructionId: other.constructionId,
            edge,
          });
        }
      }
    }
  }
  if (!changed) return linesAfter;
  return linesAfter.map((l) => state.get(l.id) ?? l);
}

/** Apply Snap enforcement after a *group* of construction lines has
 *  all translated together by the same delta (issue #80 slice 2 —
 *  group-as-rigid-object). Every line outside the group that's
 *  pinned to any of the moved members via a Snap constraint
 *  follows by the same delta; internal member ↔ member constraints
 *  stay satisfied for free because all members moved as one.
 *
 *  The caller is responsible for translating the member lines
 *  themselves in `lines` before invoking this — same contract as
 *  `propagateCoincidentAfterLineMove`. */
export function propagateCoincidentAfterGroupMove(
  proj: Project,
  lines: ConstructionLine[],
  movedConstructionIds: readonly string[],
  scope: paper.PaperScope,
): ConstructionLine[] {
  if (movedConstructionIds.length === 0) return lines;
  const originIds = new Set(movedConstructionIds);
  const active: SnapRefLite[] = [];
  for (const id of movedConstructionIds) {
    const moved = lines.find((l) => l.id === id);
    if (!moved) continue;
    for (const a of allAnchorsOnLine(moved, scope)) {
      active.push({
        kind: "anchor",
        constructionId: id,
        pathIndex: a.pathIndex,
        segmentIndex: a.segmentIndex,
      });
    }
    if (moved.primitive) {
      active.push({ kind: "center", constructionId: id });
      for (const edge of BBOX_EDGES) {
        active.push({ kind: "bbox-midpoint", constructionId: id, edge });
      }
    }
  }
  return applySnapChainTranslation(proj, lines, active, originIds, scope);
}

/** Apply Snap (coincident-anchor) enforcement after the user has
 *  moved a *whole* construction line (translation drag) or reshaped it
 *  as a whole (a resize, #1166). Every line connected to the moved one
 *  through Snap constraints — directly or transitively — is translated
 *  so it lands back on the ref it is pinned to; each step reads the
 *  ref's CURRENT position, so partners on different corners of a
 *  resized rectangle each get their own delta. Partners keep their
 *  parametric primitives.
 *
 *  The moved line itself is already at its post-edit state in `lines`
 *  and is never touched again (no chain-through-origin pull). A partner
 *  pinned to two of its anchors moves with the first and has its other
 *  linked anchor pulled onto the second — it stretches, keeping both
 *  ends attached (chain-through-partner). */
export function propagateCoincidentAfterLineMove(
  proj: Project,
  lines: ConstructionLine[],
  movedConstructionId: string,
  scope: paper.PaperScope,
): ConstructionLine[] {
  const movedLine = lines.find((l) => l.id === movedConstructionId);
  if (!movedLine) return lines;
  // Every snap-ref on the moved line is an active source: every
  // anchor (they all translated together with the line) plus the
  // bbox centre when the line still has its primitive — both move
  // with the line, both should drive partner translations.
  const active: SnapRefLite[] = allAnchorsOnLine(movedLine, scope).map((a) => ({
    kind: "anchor",
    constructionId: movedConstructionId,
    pathIndex: a.pathIndex,
    segmentIndex: a.segmentIndex,
  }));
  if (movedLine.primitive) {
    active.push({ kind: "center", constructionId: movedConstructionId });
    for (const edge of BBOX_EDGES) {
      active.push({
        kind: "bbox-midpoint",
        constructionId: movedConstructionId,
        edge,
      });
    }
  }
  return applySnapChainTranslation(
    proj,
    lines,
    active,
    new Set([movedConstructionId]),
    scope,
  );
}

/** True when this constraint has a wired-up enforcement rule, so the
 *  per-row "Apply" button in the Properties panel can actually act
 *  on it. Today coincident with an anchor as its first ref is the
 *  only such kind; every other kind lands as decorative metadata
 *  until its own propagation rule ships. */
export function isConstraintEnforced(constraint: Constraint): boolean {
  if (constraint.kind !== "coincident") return false;
  // The propagator uses the first ref's anchor position as the
  // snap target every other ref is pulled toward — so the first ref
  // must be an anchor for there to be anything to apply.
  const first = constraint.refs[0];
  return first !== undefined && first.kind === "anchor";
}

/** One-shot enforcement of `constraint`: rigidly translates every
 *  partner line so its coincident-paired ref lands back on the first
 *  ref's current anchor position. Returns the (possibly mutated)
 *  line list — the caller wraps this in a `ctx.commit`. Used by the
 *  Properties panel's per-row Apply button so a stale or conflicting
 *  pair can be "resolved" against this constraint without dragging.
 *
 *  Returns `lines` unchanged when the kind has no enforcement rule
 *  (mirrors `isConstraintEnforced`). */
export function applyConstraint(
  proj: Project,
  constraint: Constraint,
  lines: ConstructionLine[],
  scope: paper.PaperScope,
): ConstructionLine[] {
  if (!isConstraintEnforced(constraint)) return lines;
  const firstRef = constraint.refs[0]!;
  if (firstRef.kind !== "anchor") return lines;
  return propagateCoincidentAfterAnchorDrag(
    proj,
    lines,
    {
      constructionId: firstRef.constructionId,
      pathIndex: firstRef.pathIndex,
      segmentIndex: firstRef.segmentIndex,
    },
    scope,
  );
}

/** Apply Snap (coincident-anchor) enforcement to `linesAfter` given
 *  that the user just dragged the anchor at `dragged` to a new
 *  position. Every partner line connected to that anchor through a
 *  Snap constraint — directly or transitively via other Snap
 *  constraints between partner lines — is rigidly translated so the
 *  chain stays satisfied. The dragged line itself is left as-edited;
 *  only the single dragged anchor moved within it. Partners keep
 *  their parametric primitives, since translation is a
 *  primitive-preserving edit. */
export function propagateCoincidentAfterAnchorDrag(
  proj: Project,
  linesAfter: ConstructionLine[],
  dragged: {
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
  },
  scope: paper.PaperScope,
): ConstructionLine[] {
  const draggedRef: AnchorRefLite = {
    kind: "anchor",
    constructionId: dragged.constructionId,
    pathIndex: dragged.pathIndex,
    segmentIndex: dragged.segmentIndex,
  };
  return applySnapChainTranslation(
    proj,
    linesAfter,
    [draggedRef],
    new Set([dragged.constructionId]),
    scope,
    draggedRef,
  );
}
